// The menu is the Rithmo customer dashboard's (customerBuild.ts), with its subtitle, "Control Board"; the signed-in user
// and organization arrive as `session` from Rithmo's own sign-in; and the navigation (menu button, mobile panel, desktop sidebar) is also exported on its
// own as ShellNavigation, for pages that already lay out their own main column; the active link is Rithmo's (the first
// dashboard's blue-to-teal gradient).
import { Fragment, useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Activity, Bot, LayoutDashboard, LogOut, Menu, Plug, Settings, Users, X, type LucideIcon } from 'lucide-react'
import { OrgWordmark } from './OrgMark'
import type { CustomerNavItem } from '../../customerBuild'

/** Who is signed in, from Rithmo's session: the shell shows these and nothing else. */
export interface ShellSession {
  user: { name?: string | null; email?: string | null } | null
  workspaceName: string | null
  items: CustomerNavItem[]
  signOut: () => void | Promise<void>
}

/** The Control Board's icons (CONTROL_BOARD_NAV), and Settings. */
const ICONS: Record<string, LucideIcon> = {
  '/dashboard': Activity,
  '/agents': Bot,
  '/my-agents': Bot,
  '/connections': Plug,
  '/authority': Users,
  '/settings': Settings,
}

function SidebarContent({ session, onNavigate, onClose }: { session: ShellSession; onNavigate?: () => void; onClose?: () => void }) {
  const { user, workspaceName, signOut, items } = session
  const navigate = useNavigate()

  return (
    <>
      <div className="flex h-20 items-center justify-between border-b border-gray-300 px-5">
        <OrgWordmark name={workspaceName || 'Your organization'} subtitle="Control Board" />
        {onClose && (
          <button onClick={onClose} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-700" aria-label="Close menu">
            <X size={20} />
          </button>
        )}
      </div>

      <nav aria-label="Main" className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {items.map(({ label, path, group }, i) => {
          const Icon = ICONS[path] ?? LayoutDashboard
          return (
          <Fragment key={path}>
          {group === 'setup' && items[i - 1]?.group !== 'setup' && (
            <div className="px-4 pb-1 pt-5 text-[11px] font-bold uppercase tracking-wide text-gray-600">Setup</div>
          )}
          <NavLink
            to={path}
            onClick={onNavigate}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition ${
              isActive ? 'bg-brand-gradient text-white shadow-glow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <Icon size={19} />
            <span>{label}</span>
          </NavLink>
          </Fragment>
          )
        })}
      </nav>

      <div className="border-t border-gray-300 p-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-gradient text-sm font-semibold text-white">
            {(user?.name || user?.email || 'R').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{user?.name || 'Signed in'}</p>
            <p className="truncate text-xs text-gray-600">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={async () => { await signOut(); navigate('/login') }}
          className="mt-3 flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </>
  )
}

/** The menu button, the mobile panel and the desktop sidebar: the shell without its main column. */
export function ShellNavigation({ session }: { session: ShellSession }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 top-4 z-50 rounded-xl border border-gray-300 bg-white/90 p-3 text-gray-700 shadow-sm backdrop-blur lg:hidden"
        aria-label="Open menu"
      >
        <Menu size={22} />
      </button>

      {mobileOpen && (
        <div className="lg:hidden">
          <button className="fixed inset-0 z-40 bg-black/30" onClick={() => setMobileOpen(false)} aria-label="Close menu overlay" />
          <aside className="fixed bottom-0 left-0 top-0 z-50 flex w-[280px] flex-col border-r border-gray-300 bg-white">
            <SidebarContent session={session} onNavigate={() => setMobileOpen(false)} onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <aside className="fixed bottom-0 left-0 top-0 z-40 hidden w-[280px] flex-col border-r border-gray-300 bg-white/90 shadow-sm backdrop-blur-xl lg:flex">
        <SidebarContent session={session} />
      </aside>
    </>
  )
}

export function AppShell({ session, children }: { session: ShellSession; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <ShellNavigation session={session} />

      <main className="p-4 pt-20 lg:ml-[280px] lg:p-8">
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  )
}
